import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import {required} from '@vuelidate/validators';
import {ActionService} from '../../api';

export default class AlertDefinition extends BaseModel {
  constructor({
    id,
    designation = undefined,
    alertType = undefined,
    active = true,
    permanent = false,
    allEquipments = false,
    alertDefinition = undefined,
    mailRecipients = undefined,
    coolDownPeriod = undefined,
  }) {
    super(id, undefined, designation);
    this.alertType = alertType
      ? HitOnlineEnums.getEnumValue('ALERT_TYPE', alertType, translate)
      : null;
    this.active = active;
    this.permanent = permanent;
    this.allEquipments = allEquipments;
    this.alertDefinition = alertDefinition || {};
    this.mailRecipients = mailRecipients;
    this.coolDownPeriod = coolDownPeriod;
  }

  /**
   * Ask the store consumer to drop cached alert definitions for the current company.
   * Fire-and-forget: UI CRUD must not fail if the queue notification fails.
   */
  static clearStoreCache() {
    return ActionService.post('tracking/clear-alert-cache', {}).catch(
      (error) => {
        console.error('Failed to clear tracking alert cache', error);
      },
    );
  }

  async delete(router = undefined) {
    const deleted = await super.delete(router);
    if (deleted !== false) {
      await AlertDefinition.clearStoreCache();
    }
    return deleted;
  }

  static get config() {
    const config = BaseModel.config;
    config['importable'] = false;
    return config;
  }

  get config() {
    return AlertDefinition.config;
  }

  static get baseModuleForRedirection() {
    return 'tracking-base';
  }

  get baseModuleForRedirection() {
    return AlertDefinition.baseModuleForRedirection;
  }

  static get fkName() {
    return 'alert_definition_id';
  }

  static get apiRoute() {
    return 'alert_definition';
  }

  static get listPageRoute() {
    return 'alertList';
  }

  static get detailPageRoute() {
    return 'alertDetail';
  }

  static get createInitColumns() {
    return {
      designation: '',
      alert_type: 'TRACKING_MAX_SPEED',
      active: true,
      permanent: true,
      all_equipments: true,
      alert_definition: {},
      mail_recipients: '',
      cool_down_period: 0,
    };
  }

  get fkName() {
    return AlertDefinition.fkName;
  }

  get apiRoute() {
    return AlertDefinition.apiRoute;
  }

  get listPageRoute() {
    return AlertDefinition.listPageRoute;
  }

  get detailPageRoute() {
    return AlertDefinition.detailPageRoute;
  }

  get createInitColumns() {
    return AlertDefinition.createInitColumns;
  }

  static get joinAttrsKey() {
    return ['designation', 'alertType', 'active'];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'designation',
      'alertType',
      'active',
      'permanent',
      'allEquipments',
      'coolDownPeriod',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(['designation', 'alertType', 'active']);
  }

  static get allAttributes() {
    return [
      this.DESIGNATION_ATTRIBUTE({validations: {required}}),
      this.ACTIVE_ATTRIBUTE(),
      new AT.BooleanAttribute({
        name: 'permanent',
        column: 'permanent',
        tableProperties: {
          header: 'hit-app.alert.permanent',
        },
      }),
      new AT.BooleanAttribute({
        name: 'allEquipments',
        column: 'all_equipments',
        tableProperties: {
          header: 'hit-app.alert.all-equipments',
        },
      }),
      new AT.StringAttribute({
        name: 'mailRecipients',
        column: 'mail_recipients',
        validations: {required},
        tableProperties: {
          header: 'hit-app.alert.mail-recipients',
        },
      }),
      new AT.IntegerAttribute({
        name: 'coolDownPeriod',
        column: 'cool_down_period',
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.alert.cool-down-period',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'alertType',
        column: 'alert_type',
        enumType: 'ALERT_TYPE',
        enumClass: HitOnlineEnums,
        validations: {required},
        tableProperties: {
          header: 'hit-app.alert.alert-type',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'alertDefinition',
        column: 'alert_definition',
        creationValue: {},
      }),
    ];
  }
}
